import FacilityActions from '../actions/FacilityActions';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
interface UrlSyncManagerConfig {
    debounceMs?: number;
}

// Watches store changes and mirrors state into the URL query string.
class UrlSyncManager {
    private _isRunning: boolean = false;
    private _lastParams: QueryParams = {};
    private _debounceHandle: number | null = null;
    private _config: UrlSyncManagerConfig;

    constructor(config?: UrlSyncManagerConfig) {
        this._config = {
            debounceMs: 75,
            ...(config || {})
        };
    }

    start() {
        if (this._isRunning) {
            return;
        }

        this._isRunning = true;
        FacilityStore.addChangeListener(this._handleStoreChange);
        ViewerStore.addChangeListener(this._handleStoreChange);
        KnowledgeBookStore.addChangeListener(this._handleStoreChange);

        this._syncNow();
    }

    stop() {
        if (!this._isRunning) {
            return;
        }

        this._isRunning = false;
        FacilityStore.removeChangeListener(this._handleStoreChange);
        ViewerStore.removeChangeListener(this._handleStoreChange);
        KnowledgeBookStore.removeChangeListener(this._handleStoreChange);
        this._clearDebounce();
    }

    // Exposed for testing or manual refreshes.
    syncImmediate() {
        this._syncNow();
    }

    private _handleStoreChange = () => {
        if (!this._isRunning) {
            return;
        }

        this._clearDebounce();
        this._debounceHandle = window.setTimeout(() => {
            this._debounceHandle = null;
            this._syncNow();
        }, this._config.debounceMs);
    }

    private _clearDebounce() {
        if (this._debounceHandle !== null) {
            window.clearTimeout(this._debounceHandle);
            this._debounceHandle = null;
        }
    }

    private _syncNow() {
        const nextParams = this._deriveParams();
        if (!this._hasMeaningfulChange(this._lastParams, nextParams)) {
            return;
        }

        this._lastParams = { ...nextParams };
        this._updateURL(nextParams);
    }

    private _deriveParams(): QueryParams {
        const selectedFacility = FacilityStore.getSelectedFacility();
        const selectedOntology = FacilityStore.getSelectedOntology();
        const selectedView = ViewerStore.getSelectedViewImage();
        const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        const selectedAssetSet = ViewerStore.getSelectedAssetSet();
        const selectedKnowledgeBook = KnowledgeBookStore.getSelectedKnowledgeBook();
        const knowledgeLibraryCardUrlState = FacilityStore.getKnowledgeLibraryCardUrlState();
        const selectedHierarchyItem = ViewerStore.getSelectedHierarchyItem()?.ID;
        const selectedHierarchyItems = ViewerStore.getSelectedHierarchyItems();
        const rangeView = ViewerStore.getRangeView();
        const isInRangeView = ViewerStore.isInRangeView();
        const isViewerVisible = ViewerStore.is3DViewerVisible() || ViewerStore.isPanoViewerVisible();
        // The library stays open underneath the viewer so it can be returned to.
        // Only use its card URL state while the library is actually visible.
        const isKnowledgeLibraryOpen = FacilityStore.getKnowledgeLibraryOpen() && !isViewerVisible;
        const multiSelectEnabled = ViewerStore.getAllowMultiSelect();
        let selectedAnnotation = ViewerStore.getSelectedAnnotation();
        
        if (selectedAnnotation) {
            // Verify that the selected annotation is an asset tag (and not some other type of annotation).
            if (!selectedFacility?.AssetTags?.find(tag => tag.ID === selectedAnnotation)) {
                selectedAnnotation = null;
            }
        }

        const params: QueryParams = {};

        if (selectedFacility?.ID) {
            params.facilityId = selectedFacility.ID;
        }

        if (selectedView?.ID) {
            params.viewId = selectedView.ID;
        }

        // Manage Asset Sets closes the active view but keeps its ID for restoration.
        // Keep it while the Knowledge Views panel restores that view asynchronously.
        // Use only a saved view belonging to the currently open book.
        const savedKnowledgeView = isViewerVisible && selectedKnowledgeBook?.ID &&
            (FacilityStore.getFacilityAssetSetsBarOpen() || FacilityStore.getKnowledgeBookbarOpen() ||
                FacilityStore.getKnowledgeViewEditBarOpen() || selectedAssetSet?.ID)
            ? KnowledgeBookStore.getAllKnowledgeView()?.find(view =>
                view.ID === ViewerStore.getSavedKnowledgeView() && view.KnowledgeBookID === selectedKnowledgeBook.ID)
            : undefined;
        const knowledgeViewId = selectedKnowledgeView?.ID || savedKnowledgeView?.ID;
        if (knowledgeViewId) {
            params.kvId = knowledgeViewId;
        }

        if (isKnowledgeLibraryOpen && knowledgeLibraryCardUrlState.isCardView) {
            if (knowledgeLibraryCardUrlState.knowledgeBookId) {
                params.kbId = knowledgeLibraryCardUrlState.knowledgeBookId;
            }
        } else if (!isKnowledgeLibraryOpen && selectedKnowledgeBook?.ID && !knowledgeViewId) {
            params.kbId = selectedKnowledgeBook.ID;
        }

        if (isKnowledgeLibraryOpen && knowledgeLibraryCardUrlState.isCardView && knowledgeLibraryCardUrlState.classificationTagId) {
            params.cltagId = knowledgeLibraryCardUrlState.classificationTagId;
        }

        if (isViewerVisible && selectedAssetSet?.ID) {
            params.assetSetId = selectedAssetSet.ID;
        }

        if (selectedHierarchyItem || selectedAnnotation) {
            params.assetId = (!multiSelectEnabled || selectedHierarchyItems.length == 1) && (selectedHierarchyItem || selectedAnnotation);
        }

        if (selectedOntology?.OntologyParentID) {
            params.ontologyId = selectedOntology.ID;
        }

        if (rangeView?.ID) {
            params.rangeViewId = rangeView.ID;
        }

        if (ViewerStore.is3DViewerVisible()) {
            const kbViewingMode = KnowledgeBookStore.getKnowledgeBookViewingMode();
            if (selectedKnowledgeBook?.ID && kbViewingMode === 'manageAssets' && !selectedAssetSet?.ID) {
                params.mode = '3DSet';
            } else if (isInRangeView) {
                params.mode = '3DRange';
            } else {
                params.mode = '3D';
            }
        } else if (ViewerStore.isPanoViewerVisible()) {
            params.mode = '360';
        } else if (isKnowledgeLibraryOpen && !ViewerStore.isPanoViewerVisible()) {
            params.mode = knowledgeLibraryCardUrlState.isCardView ? 'klibcard' : 'klib';
        } else {
            params.mode = 'exp';
        }

        return params;
    }

    private _hasMeaningfulChange(prev: QueryParams, next: QueryParams) {
        const keys: (keyof QueryParams)[] = ['facilityId', 'viewId', 'kbId', 'kvId', 'assetSetId', 'rangeViewId', 'assetId', 'cltagId', 'ontologyId', 'mode'];
        for (const key of keys) {
            const previous = prev[key] ?? '';
            const upcoming = next[key] ?? '';
            if (previous !== upcoming) {
                return true;
            }
        }
        return false;
    }

    private _updateURL(params: QueryParams) {
        
        const currentUrl = window.location.href.split('?')[0];
        const urlSearchParams = new URLSearchParams(window.location.search);

        const facilityId = urlSearchParams.get('facilityId') || '';
        const viewId = urlSearchParams.get('viewId') || '';
        const kbId = urlSearchParams.get('kbId') || '';
        const kvId = urlSearchParams.get('kvId') || '';
        const rangeViewId = urlSearchParams.get('rangeViewId') || '';
        const assetSetId = urlSearchParams.get('assetSetId') || '';
        const assetId = urlSearchParams.get('assetId') || '';
        const cltagId = urlSearchParams.get('cltagId') || '';
        const ontologyId = urlSearchParams.get('ontologyId') || '';
        const mode = urlSearchParams.get('mode') || '';

        let flag = false;
        let assetIdChanged = false;

        let newOntologyId = params.ontologyId;
        let newMode = params.mode;
    const isInRangeView = ViewerStore.isInRangeView();

        const setOntologyParam = () => {
            // If ontologyId is not provided in params, try to get it from the selected ontology.
            // We want to place the selected ontology into the URL if a non-root ontology is selected.
            if (!newOntologyId) {
                const selectedOntology = FacilityStore.getSelectedOntology();
                if (selectedOntology?.OntologyParentID) {
                    newOntologyId = selectedOntology.ID;
                }
            }
        }

        const setModeParam = () => {
            if (!newMode) {
                if (ViewerStore.is3DViewerVisible()) {
                    const selectedKnowledgeBook = KnowledgeBookStore.getSelectedKnowledgeBook();
                    const selectedAssetSet = ViewerStore.getSelectedAssetSet();
                    const kbViewingMode = KnowledgeBookStore.getKnowledgeBookViewingMode();
                    if (selectedKnowledgeBook?.ID && kbViewingMode === 'manageAssets' && !selectedAssetSet?.ID) {
                        newMode = '3DSet';
                    } else if (isInRangeView) {
                        newMode = '3DRange';
                    } else {
                        newMode = '3D';
                    }
                } else if (ViewerStore.isPanoViewerVisible()) {
                    newMode = '360';
                } else if (FacilityStore.getKnowledgeLibraryOpen()) {
                    newMode = 'klib';
                } 
                else {
                    newMode = 'exp';
                }
            }
        }

        if (params.facilityId !== undefined) {
            if (facilityId !== params.facilityId) {
                urlSearchParams.set('facilityId', params.facilityId);
                flag = true;
            }
            setOntologyParam();
            setModeParam();
        } else if (facilityId) {
            urlSearchParams.delete('facilityId');
            flag = true;
        }

        if (params.viewId !== undefined) {
            if (viewId !== params.viewId) {
                urlSearchParams.set('viewId', params.viewId);
                flag = true;
            }
            setOntologyParam();
            setModeParam();
        } else if (viewId) {
            urlSearchParams.delete('viewId');
            flag = true;
        }

        if (params.kbId !== undefined) {
            if (kbId !== params.kbId) {
                urlSearchParams.set('kbId', params.kbId);
                flag = true;
            }
            setOntologyParam();
            setModeParam();
        } else if (kbId) {
            urlSearchParams.delete('kbId');
            flag = true;
        }

        if (params.kvId !== undefined) {
            if (kvId !== params.kvId) {
                urlSearchParams.set('kvId', params.kvId);
                flag = true;
            }
            setOntologyParam();
            setModeParam();
        } else if (kvId) {
            urlSearchParams.delete('kvId');
            flag = true;
        }

        if (params.rangeViewId !== undefined) {
            if (rangeViewId !== params.rangeViewId) {
                urlSearchParams.set('rangeViewId', params.rangeViewId);
                flag = true;
            }
        } else if (rangeViewId) {
            urlSearchParams.delete('rangeViewId');
            flag = true;
        }

        if (params.assetSetId !== undefined) {
            if (assetSetId !== params.assetSetId) {
                urlSearchParams.set('assetSetId', params.assetSetId);
                flag = true;
            }
        } else if (assetSetId) {
            urlSearchParams.delete('assetSetId');
            flag = true;
        }

        if (params.assetId !== undefined) {
            if (assetId !== params.assetId) {
                urlSearchParams.set('assetId', params.assetId);
                assetIdChanged = true;
            }
        } else if (assetId) {
            urlSearchParams.delete('assetId');
            assetIdChanged = true;
        }

        if (params.cltagId !== undefined) {
            if (cltagId !== params.cltagId) {
                urlSearchParams.set('cltagId', params.cltagId);
                flag = true;
            }
        } else if (cltagId) {
            urlSearchParams.delete('cltagId');
            flag = true;
        }

        if (newOntologyId !== undefined && newOntologyId !== "") {
            if (ontologyId !== newOntologyId) {
                urlSearchParams.set('ontologyId', newOntologyId);
                flag = true;
            }
        } else if (ontologyId) {
            urlSearchParams.delete('ontologyId');
            flag = true;
        }

        if (newMode !== undefined && newMode !== "exp") {
            if (mode !== newMode) {
                urlSearchParams.set('mode', newMode);
                flag = true;
            }
        } else if (mode) {
            urlSearchParams.delete('mode');
            flag = true;
        }

        if (flag || assetIdChanged) {
            const newUrl = `${currentUrl}?${urlSearchParams.toString()}`;
            if (assetIdChanged && !flag) {
                // Only assetId changed, and no other changes
                window.history.replaceState({}, '', newUrl);
            } else {
                // Other changes, use pushState
                window.history.pushState({}, '', newUrl);
            }
        }
        FacilityActions.updateContextHelp(params);
    }
}

const urlSyncManager = new UrlSyncManager();

export default urlSyncManager;
